/**
 * What a property owner may *do* with a file they have been granted access to,
 * beyond reading it.
 *
 * The grant itself (`citizen_file_access`) is the right to see the file: its
 * timeline, its milestones, the documents that exist on it. These are the
 * actions on top of that, each independently withdrawable — so an officer who
 * needs to stop one behaviour is not forced to choose between tolerating it and
 * cutting the owner off from their own enforcement record entirely.
 *
 * **Why viewing is not in this list.** It would be a second way to express what
 * `revoked_at` already says, and two switches meaning "can this person see the
 * file" is exactly how they come to disagree. Withdrawing sight of the file is
 * a revocation and belongs on the grant.
 *
 * The list is deliberately short. It names the actions the portal actually
 * offers today; the ones a longer roadmap imagines — uploading remediation
 * photos, asking for a deadline extension, acknowledging a notice — get their
 * entry when the action they gate exists. A toggle that gates nothing teaches an
 * officer that the switches here are decorative.
 */
export const CITIZEN_CAPABILITIES = ['request_documents'] as const;

export type CitizenCapability = (typeof CITIZEN_CAPABILITIES)[number];

const CAPABILITY_SET: ReadonlySet<string> = new Set(CITIZEN_CAPABILITIES);

export function isCitizenCapability(value: string): value is CitizenCapability {
  return CAPABILITY_SET.has(value);
}

/**
 * What a grant confers when nobody has said otherwise.
 *
 * Everything. An owner given access to their file could already request
 * documents before capabilities existed, and a migration that quietly took that
 * away from every existing grant would be a behaviour change disguised as a
 * schema change. New grants start here too: the officer's decision is to
 * *withdraw* something, which is a decision they make about a person, not a
 * checklist they must complete before access is useful.
 */
export const DEFAULT_CITIZEN_CAPABILITIES: readonly CitizenCapability[] = CITIZEN_CAPABILITIES;

/**
 * Read a stored capability list back into the vocabulary.
 *
 * Unknown entries are dropped rather than trusted — a capability removed from
 * the product must stop granting anything, even where a row still names it.
 * A null column (every row written before this existed) means the default.
 */
export function normalizeCitizenCapabilities(
  stored: readonly string[] | null | undefined,
): CitizenCapability[] {
  if (stored === null || stored === undefined) return [...DEFAULT_CITIZEN_CAPABILITIES];
  return CITIZEN_CAPABILITIES.filter((capability) => stored.includes(capability));
}

/** Whether a grant carrying `stored` permits `capability`. */
export function grantAllows(
  stored: readonly string[] | null | undefined,
  capability: CitizenCapability,
): boolean {
  return normalizeCitizenCapabilities(stored).includes(capability);
}
